
import { Link } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { blogPosts } from "@/data/blog-posts";
import { useAuth } from "@/lib/auth";

// Create a custom ScrollToTopLink component 
const ScrollToTopLink = ({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) => {
  const handleClick = () => {
    // Scroll to top when link is clicked
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  
  return (
    <Link to={to} className={className} onClick={handleClick}>
      {children}
    </Link>
  );
};

export const Footer = () => {
  const { user } = useAuth();
  
  // Get 3 most recent blog posts sorted by published date
  const recentPosts = useMemo(() => {
    return [...blogPosts]
      .sort((a, b) => b.publishedAt.getTime() - a.publishedAt.getTime())
      .slice(0, 3);
  }, []);

  return (
    <footer className="border-t border-border bg-background py-12">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 lg:gap-12">
          {/* Company section */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <ScrollToTopLink to="/">
                <img src="/assets/icons/branding/chronocal-logo.svg" alt="ChronoCal Logo" className="h-8 w-auto" />
              </ScrollToTopLink>
            </div>
            <p className="text-muted-foreground text-sm">
              Energy-aware appointment scheduler. End the email ping-pong, and protect your most productive hours.
            </p>
          </div>
          
          {/* Product section */}
          <div>
            <h3 className="font-medium text-sm mb-4 text-foreground">Product</h3>
            <ul className="space-y-2 text-sm">
              <li><ScrollToTopLink to="/" className="text-muted-foreground hover:text-foreground transition-colors">Home</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/features" className="text-muted-foreground hover:text-foreground transition-colors">Features</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">Pricing</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/science" className="text-muted-foreground hover:text-foreground transition-colors">The Science</ScrollToTopLink></li>
            </ul>
          </div>

          {/* Integrations section */}
          <div>
            <h3 className="font-medium text-sm mb-4 text-foreground">Integrations</h3>
            <ul className="space-y-2 text-sm">
              <li><ScrollToTopLink to="/integrations/biostrap" className="text-muted-foreground hover:text-foreground transition-colors">Biostrap</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/integrations/oura" className="text-muted-foreground hover:text-foreground transition-colors">Oura</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/integrations/whoop" className="text-muted-foreground hover:text-foreground transition-colors">Whoop</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/integrations/ultrahuman" className="text-muted-foreground hover:text-foreground transition-colors">Ultrahuman</ScrollToTopLink></li>
            </ul>
          </div>
          
          {/* Solutions section */}
          <div>
            <h3 className="font-medium text-sm mb-4 text-foreground">Resources</h3>
            <ul className="space-y-2 text-sm">
              <li><ScrollToTopLink to="/hacks" className="text-muted-foreground hover:text-foreground transition-colors">125+ Productivity Hacks</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/compare" className="text-muted-foreground hover:text-foreground transition-colors">Compare All Tools</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/compare/chronocal-vs-calendly" className="text-muted-foreground hover:text-foreground transition-colors">ChronoCal vs Calendly</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/compare/chronocal-vs-reclaim" className="text-muted-foreground hover:text-foreground transition-colors">ChronoCal vs Reclaim</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/compare/chronocal-vs-clockwise" className="text-muted-foreground hover:text-foreground transition-colors">ChronoCal vs Clockwise</ScrollToTopLink></li>              
            </ul>
          </div>


          {/* Company section */}
          <div>
            <h3 className="font-medium text-sm mb-4 text-foreground">Company</h3>
            <ul className="space-y-2 text-sm">
              <li><ScrollToTopLink to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">Blog</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/about" className="text-muted-foreground hover:text-foreground transition-colors">About</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/careers" className="text-muted-foreground hover:text-foreground transition-colors">Careers</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">Terms</ScrollToTopLink></li>
            </ul>
          </div>
          
          {/* Support section */}
          <div>
            <h3 className="font-medium text-sm mb-4 text-foreground">Support</h3>
            <ul className="space-y-2 text-sm">
              <li><ScrollToTopLink to="/help" className="text-muted-foreground hover:text-foreground transition-colors">Help Center</ScrollToTopLink></li>
              <li><ScrollToTopLink to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact</ScrollToTopLink></li>
            </ul>
          </div>
        </div>
        
        {/* Copyright section */}
        <div className="mt-12 pt-6 border-t border-border text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} ChronoCal. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};
